import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  EditorialLedger,
  Pin,
  PinNote,
  PresenceDot,
  Reveal,
  SectionHead,
  StatCounter,
} from '@/design-system'
import { beats, ledgerRows, photo, proofStats, threadRows } from './data'
import { APP_NAME } from '@/utils/constants'
import { Sec } from './SectionShell'
import { AlreadySharedSection } from './AlreadySharedSection'

/* ---------- 01 FIDELITY ---------- */

const FidGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
  gap: 'clamp(28px, 4vw, 72px)',
  paddingTop: 44,
  alignItems: 'center',
  '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
})

const Story = styled(Box)({
  fontSize: 15.5,
  lineHeight: 1.66,
  color: 'var(--ds-ink-muted)',
  maxWidth: 420,
  '& b': { color: 'var(--ds-ink)', fontWeight: 650 },
})

const Visual = styled(Box)({
  position: 'relative',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-deep)',
})

const VisualImg = styled('img')({
  width: '100%',
  aspectRatio: '16 / 10.5',
  objectFit: 'cover',
  display: 'block',
})

const Loupe = styled(Box)({
  position: 'absolute',
  right: '9%',
  top: '14%',
  width: 148,
  height: 148,
  borderRadius: '50%',
  border: '2.5px solid #fff',
  boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
  overflow: 'hidden',
  background: '#000',
  '@media (max-width: 640px)': { width: 96, height: 96, right: '5%', top: '10%' },
})

const LoupeImg = styled('img')({
  position: 'absolute',
  width: '480%',
  maxWidth: 'none',
  left: '-270%',
  top: '-60%',
})

const LoupeTag = styled(Box)({
  position: 'absolute',
  left: '50%',
  bottom: 9,
  transform: 'translateX(-50%)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7,
  letterSpacing: '0.12em',
  color: '#fff',
  background: 'rgba(0,0,0,0.55)',
  borderRadius: 'var(--ds-radius-pill)',
  padding: '3px 8px',
  whiteSpace: 'nowrap',
})

const Cap = styled(Box)({
  position: 'absolute',
  left: 14,
  bottom: 12,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.12em',
  color: 'rgba(255,255,255,0.85)',
  background: 'rgba(0,0,0,0.45)',
  backdropFilter: 'blur(6px)',
  padding: '5px 10px',
  borderRadius: 'var(--ds-radius-pill)',
})

function FidelitySection() {
  return (
    <Sec id='fidelity'>
      <Reveal>
        <SectionHead
          index={1}
          title='Every pixel survives.'
          aside={
            <>
              RAW · HEIC · JPEG
              <br />
              BIT-PERFECT ORIGINALS
            </>
          }
        />
      </Reveal>
      <FidGrid>
        <Reveal>
          <Story>
            <p>
              Send a wedding photo through a chat app and <b>92% of it dies in transit</b>. Post it
              to a feed and the platform decides what your memory looks like. {APP_NAME} stores the
              original and serves it beautifully — the ledger below is the whole policy.
            </p>
            <EditorialLedger rows={ledgerRows} />
          </Story>
        </Reveal>
        <Reveal delay={100}>
          <Visual>
            <VisualImg src={photo('a01-wedding')} alt='' />
            <Loupe>
              <LoupeImg src={photo('a01-wedding')} alt='' />
              <LoupeTag>400% · STILL SHARP</LoupeTag>
            </Loupe>
            <Cap>ORIGINAL · 6240×4160 · 24.6MP</Cap>
          </Visual>
        </Reveal>
      </FidGrid>
    </Sec>
  )
}

/* ---------- 02 INTERACTIONS ---------- */

const NightStage = styled(Box)({
  marginTop: 46,
  borderRadius: 'var(--ds-radius-xl)',
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse 50% 65% at 22% 20%, rgba(190,157,253,0.14), transparent 62%)',
    pointerEvents: 'none',
  },
})

const StageGrid = styled(Box)({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
  gap: 'clamp(24px, 3.5vw, 56px)',
  padding: 'clamp(28px, 4vw, 56px)',
  alignItems: 'center',
  '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
})

const Frame = styled(Box)({
  position: 'relative',
  borderRadius: 14,
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-night)',
})

const FrameImg = styled('img')({ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' })

const Sig = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.16em',
  color: '#ffcf7d',
  textTransform: 'uppercase',
})

const StageH3 = styled('h3')({
  margin: 0,
  marginTop: 14,
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(24px, 2.8vw, 34px)',
  fontWeight: 730,
  letterSpacing: '-0.035em',
  lineHeight: 1.08,
  color: 'var(--ds-night-ink)',
})

const StageP = styled('p')({
  marginTop: 14,
  fontSize: 14,
  lineHeight: 1.65,
  color: 'var(--ds-night-ink-muted)',
  maxWidth: 380,
})

const Thread = styled(Box)({ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 8 })

const ThreadRow = styled(Box, {
  shouldForwardProp: (p) => p !== 'visible',
})<{ visible: boolean }>(({ visible }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '11px 14px',
  borderRadius: 12,
  background: 'rgba(244,243,246,0.05)',
  border: '1px solid var(--ds-night-border)',
  fontSize: 12.5,
  color: 'var(--ds-night-ink-muted)',
  opacity: visible ? 1 : 0,
  transform: visible ? 'none' : 'translateY(8px)',
  transition: 'all 500ms var(--ds-ease-out)',
}))

function InteractionsSection() {
  const { ref, inView } = useThreadReveal()

  return (
    <Sec id='interactions'>
      <Reveal>
        <SectionHead
          index={2}
          title='Comments that live on the photo.'
          aside={
            <>
              PINNED NOTES · VOICE
              <br />
              TAPBACKS · PRESENCE
            </>
          }
        />
      </Reveal>
      <Reveal delay={80}>
        <NightStage>
          <StageGrid>
            <Frame>
              <FrameImg src={photo('a05-birthday')} alt='' />
              <Pin tone='gold' label='S' anchor={{ left: '21%', top: '28%' }} />
              <Pin tone='violet' label='▶' anchor={{ left: '66%', top: '60%' }} />
              <PinNote
                avatarLabel='S'
                name='Sarah'
                note='The face right before the candles went out 😭'
                anchor={{ left: '25%', top: '6%' }}
              />
            </Frame>
            <Box>
              <Sig>NOT A LIKE COUNTER — A CONVERSATION</Sig>
              <StageH3>Pinned to the exact moment it's about.</StageH3>
              <StageP>
                Notes anchor to a spot in the frame. Voice notes hang beside the faces they mention.
                And you can see who's standing in the album with you, right now.
              </StageP>
              <Thread ref={ref}>
                {threadRows.map((row, i) => (
                  <ThreadRow key={row.time} visible={inView}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        fontSize: 9,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        background: row.tone,
                        color: row.ink,
                      }}
                    >
                      {row.avatar}
                    </Box>
                    <Box dangerouslySetInnerHTML={{ __html: row.text }} sx={{ '& b': { color: 'var(--ds-night-ink)', fontWeight: 640 } }} />
                    <Box sx={{ marginLeft: 'auto', fontFamily: 'var(--ds-font-mono)', fontSize: 8, color: 'var(--ds-night-ink-subtle)' }}>
                      {row.time}
                    </Box>
                  </ThreadRow>
                ))}
              </Thread>
            </Box>
          </StageGrid>
        </NightStage>
      </Reveal>
    </Sec>
  )
}

function useThreadReveal() {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return { ref, inView }
}

/* ---------- 03 THE KEY ---------- */

const Beats = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  paddingTop: 44,
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr' },
})

const Beat = styled(Box)({
  position: 'relative',
  padding: '6px 28px 0 0',
})

const BeatReveal = styled(Reveal)({
  '&:not(:first-of-type)': {
    borderLeft: '1px solid var(--ds-border)',
    paddingLeft: 28,
  },
  '@media (max-width: 800px)': {
    '&:not(:first-of-type)': {
      borderLeft: 'none',
      paddingLeft: 0,
      borderTop: '1px solid var(--ds-border)',
      marginTop: 22,
      paddingTop: 22,
    },
  },
})

const BeatNo = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.16em',
  color: 'var(--ds-ink-subtle)',
})

const BeatVisual = styled(Box)({
  margin: '18px 0',
  height: 150,
  borderRadius: 14,
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--ds-hover)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const BeatH4 = styled('h4')({
  margin: 0,
  fontFamily: 'var(--ds-font)',
  fontSize: 16.5,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: 'var(--ds-ink)',
})

const BeatP = styled('p')({
  marginTop: 7,
  fontSize: 13,
  lineHeight: 1.6,
  color: 'var(--ds-ink-muted)',
  maxWidth: 300,
})

const PrintTent = styled(Box)({
  width: 84,
  height: 100,
  background: '#fff',
  borderRadius: 9,
  boxShadow: '0 12px 30px rgba(0,0,0,0.16)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  transform: 'rotate(-3deg)',
})

const ScanRet = styled(Box)({
  width: 74,
  height: 74,
  borderRadius: 16,
  border: '2px solid rgba(1,218,254,0.65)',
  position: 'relative',
  animation: 'ds-scan-pulse 2.2s var(--ds-ease) infinite',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 8,
    right: 8,
    top: '50%',
    height: 1.5,
    background: 'var(--ds-accent-deep-b)',
    boxShadow: '0 0 12px var(--ds-accent-deep-b)',
    animation: 'ds-scanline 2.2s var(--ds-ease) infinite',
  },
  '@keyframes ds-scan-pulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' } },
  '@keyframes ds-scanline': { '0%, 100%': { transform: 'translateY(-18px)' }, '50%': { transform: 'translateY(18px)' } },
})

const InsidePres = styled(Box)({
  position: 'absolute',
  left: 10,
  bottom: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  background: 'rgba(255,255,255,0.94)',
  borderRadius: 'var(--ds-radius-pill)',
  padding: '5px 11px',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7.5,
  letterSpacing: '0.1em',
  fontWeight: 600,
})

function KeySection() {
  return (
    <Sec id='key'>
      <Reveal>
        <SectionHead
          index={3}
          title='One key opens it.'
          aside={
            <>
              PRINT IT · SEND IT
              <br />
              REVOKE IT ANYTIME
            </>
          }
        />
      </Reveal>
      <Beats>
        {beats.map((beat, i) => (
          <BeatReveal key={beat.no} delay={i * 80}>
            <Beat>
              <BeatNo>{beat.no}</BeatNo>
              <BeatVisual sx={i === 0 ? { background: 'linear-gradient(160deg, #f4efe6, #e9e2d4)' } : i === 1 ? { background: 'var(--ds-night-canvas)' } : { padding: 0 }}>
                {i === 0 && (
                  <PrintTent>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 1,
                        background: 'var(--ds-night-canvas)',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 9,
                          background: 'repeating-conic-gradient(#fff 0 25%, transparent 0 50%)',
                          backgroundSize: '8px 8px',
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Box sx={{ width: 46, height: 5, borderRadius: 3, background: 'var(--ds-hover)' }} />
                    <Box sx={{ width: 30, height: 5, borderRadius: 3, background: 'var(--ds-hover)' }} />
                  </PrintTent>
                )}
                {i === 1 && <ScanRet />}
                {i === 2 && (
                  <>
                    <Box component='img' src={photo('a02-beach')} alt='' sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <InsidePres>
                      <PresenceDot tone='accent' size={6} />
                      12 PEOPLE HERE NOW
                    </InsidePres>
                  </>
                )}
              </BeatVisual>
              <BeatH4>{beat.title}</BeatH4>
              <BeatP>{beat.body}</BeatP>
            </Beat>
          </BeatReveal>
        ))}
      </Beats>
    </Sec>
  )
}

/* ---------- 04 THE ROOM ---------- */

const RoomBand = styled(Box)({
  marginTop: 46,
  position: 'relative',
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas)',
  minHeight: 580,
  display: 'flex',
  alignItems: 'flex-end',
  '@media (max-width: 900px)': { minHeight: 440 },
})

const RoomBg = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, #2c2440 0%, #191326 62%, #241c33 100%)',
})

const RoomPhoto = styled(Box)({
  position: 'absolute',
  background: '#f5f0e8',
  padding: 5,
  boxShadow: '0 24px 60px rgba(0,0,0,0.62)',
  '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
})

const RoomFg = styled(Box)({
  position: 'relative',
  zIndex: 2,
  width: '100%',
  padding: 'clamp(26px, 4vw, 52px)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 26,
  flexWrap: 'wrap',
  background: 'linear-gradient(0deg, rgba(14,13,18,0.82), transparent)',
})

const RoomH3 = styled('h3')({
  margin: 0,
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(26px, 3.4vw, 40px)',
  fontWeight: 740,
  letterSpacing: '-0.04em',
  lineHeight: 1.04,
})

const RoomP = styled('p')({
  marginTop: 10,
  fontSize: 13.5,
  color: 'var(--ds-night-ink-muted)',
  maxWidth: 430,
  lineHeight: 1.6,
})

const MonoNote = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.14em',
  color: 'var(--ds-night-ink-subtle)',
  textTransform: 'uppercase',
  marginTop: 14,
})

const ROOM_PHOTOS = [
  { key: 'a02-beach', left: '12%', top: '16%', width: '13%', ratio: '3 / 4' },
  { key: 'a01-wedding', left: '33%', top: '12%', width: '20%', ratio: '4 / 3' },
  { key: 'a06-baby', left: '61%', top: '17%', width: '13%', ratio: '3 / 4' },
  { key: 'a05-birthday', left: '80%', top: '14%', width: '14%', ratio: '4 / 3' },
]

function RoomSection() {
  const navigate = useNavigate()
  return (
    <Sec id='room'>
      <Reveal>
        <SectionHead
          index={4}
          title='Then it becomes a room.'
          aside={
            <>
              FULL 3D · RENDERED LIVE
              <br />
              SAME KEY · DEEPER VISIT
            </>
          }
        />
      </Reveal>
      <Reveal delay={80}>
        <RoomBand>
          <RoomBg>
            {ROOM_PHOTOS.map((p) => (
              <RoomPhoto key={p.key} sx={{ left: p.left, top: p.top, width: p.width, aspectRatio: p.ratio }}>
                <img src={photo(p.key)} alt='' />
              </RoomPhoto>
            ))}
          </RoomBg>
          <RoomFg>
            <Box>
              <RoomH3>
                Your album, hung in a{' '}
                <Box component='span' sx={{ background: 'var(--ds-gradient-spectrum)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  walkable gallery.
                </Box>
              </RoomH3>
              <RoomP>
                One tap converts any album into a real 3D room — frames, matting, wall color,
                lighting, all yours to art-direct. Guests walk it from the same key they already
                hold.
              </RoomP>
              <MonoNote>14 ROOMS · 73 FINISHES · 236 WALL TONES · NO 3D SKILLS</MonoNote>
            </Box>
            <Button variant='secondary' size='lg' onClick={() => navigate('/albums')} sx={{ background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' }}>
              ◈ Explore the studio →
            </Button>
          </RoomFg>
        </RoomBand>
      </Reveal>
    </Sec>
  )
}

/* ---------- 06 PROOF ---------- */

const ProofGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  paddingTop: 44,
  '@media (max-width: 800px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
})

const ProofCell = styled(Box)({
  padding: '8px 26px 4px 0',
  '&:not(:first-of-type)': { borderLeft: '1px solid var(--ds-border)', paddingLeft: 26 },
  '@media (max-width: 800px)': {
    '&:nth-of-type(3)': { borderLeft: 'none', paddingLeft: 0 },
    '&:nth-of-type(odd):not(:first-of-type)': { paddingTop: 20 },
  },
})

function ProofSection() {
  return (
    <Sec>
      <Reveal>
        <SectionHead
          index={6}
          title='Albums people return to.'
          aside={
            <>
              MEASURED ACROSS
              <br />
              SHARED ALBUMS · 90 DAYS
            </>
          }
        />
      </Reveal>
      <ProofGrid>
        {proofStats.map((stat, i) => (
          <ProofCell key={i}>
            <StatCounter value={stat.value} suffix={stat.suffix} decimals={stat.decimals} label={stat.label} source={stat.source} />
          </ProofCell>
        ))}
      </ProofGrid>
    </Sec>
  )
}

/** All 6 numbered editorial sections from Landing v2, in order. */
export function LandingSections() {
  return (
    <>
      <FidelitySection />
      <InteractionsSection />
      <KeySection />
      <RoomSection />
      <AlreadySharedSection />
      <ProofSection />
    </>
  )
}
